import { useCallback, useMemo } from 'react';
import { infiniteQueryOptions, useQueries, useQueryClient } from '@tanstack/react-query';
import sumBy from 'lodash/sumBy';
import useApiClient from 'hooks/use-api-client';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import type { CloudResourceApi } from '@env0/cloud-resource-service/api';
import type { CloudCompassAssetCountsQuery, CloudCompassAssetsQuery } from 'services/api-client/cloud-resources';

const STALE_TIME_MS = 5 * 60 * 1000;
const TEN_MINUTES_MS = 10 * 60 * 1000;
// Row lists are large: they skip structural sharing, whose deep compare on a large page costs more than it saves.
const ROW_LIST = { staleTime: STALE_TIME_MS, gcTime: TEN_MINUTES_MS, structuralSharing: false };
const DRAWER = { staleTime: TEN_MINUTES_MS, gcTime: TEN_MINUTES_MS };
// MAX_PAGES in services/cloud-resource-service/functions/get-cloud-compass-unmanaged-remainder.ts, which answers 400 past it.
export const UNMANAGED_REMAINDER_MAX_PAGES = 50;

type AssetPath = CloudResourceApi.CloudCompassAssetPath;

export const useCloudCompassQueryOptions = () => {
  const apiClient = useApiClient();
  const organizationId = useCurrentOrganizationId();

  return useMemo(
    () => ({
      filterValues: (dimension: string) => ({
        queryKey: [organizationId, 'cloud-compass', 'filter-values', dimension],
        queryFn: () => apiClient.cloudResources.getCloudCompassFilterValues(organizationId, dimension),
        staleTime: STALE_TIME_MS
      }),
      counts: (query: CloudCompassAssetCountsQuery) => ({
        queryKey: [organizationId, 'cloud-compass', 'counts', query],
        queryFn: () => apiClient.cloudResources.getCloudCompassAssetCounts(organizationId, query),
        staleTime: STALE_TIME_MS
      }),
      tree: () => ({
        queryKey: [organizationId, 'cloud-compass', 'tree'],
        queryFn: () => apiClient.cloudResources.getCloudCompassTree(organizationId),
        staleTime: STALE_TIME_MS
      }),
      unmanagedAttribution: () => ({
        queryKey: [organizationId, 'cloud-compass', 'unmanaged-attribution'],
        queryFn: () => apiClient.cloudResources.getCloudCompassUnmanagedAttribution(organizationId),
        staleTime: STALE_TIME_MS
      }),
      insights: (scope: CloudResourceApi.GetCloudCompassInsights.Scope) => ({
        queryKey: [organizationId, 'cloud-compass', 'insights', { scope }],
        queryFn: () => apiClient.cloudResources.getCloudCompassInsights(organizationId, scope),
        staleTime: STALE_TIME_MS
      }),
      managedResources: (environmentId: string) => ({
        queryKey: [organizationId, 'cloud-compass', 'state-resources', { environmentId }],
        queryFn: () => apiClient.cloudResources.getCloudCompassManagedResources(organizationId, environmentId),
        ...ROW_LIST
      }),
      unmanagedResources: (environmentId: string) => ({
        queryKey: [organizationId, 'cloud-compass', 'unmanaged-ids', { environmentId }],
        queryFn: () => apiClient.cloudResources.getCloudCompassUnmanagedResources(organizationId, environmentId),
        ...ROW_LIST
      }),
      unmanagedEnvironments: () => ({
        queryKey: [organizationId, 'cloud-compass', 'unmanaged-environments'],
        queryFn: () => apiClient.cloudResources.getCloudCompassUnmanagedEnvironments(organizationId),
        staleTime: STALE_TIME_MS
      }),
      unmanagedEnvironmentResources: (unmanagedEnvironmentId: string) => ({
        queryKey: [organizationId, 'cloud-compass', 'unmanaged-ids', { unmanagedEnvironmentId }],
        queryFn: () =>
          apiClient.cloudResources.getCloudCompassUnmanagedEnvironmentResources(organizationId, unmanagedEnvironmentId),
        ...ROW_LIST
      }),
      unmanagedRemainderTotal: () => ({
        queryKey: [organizationId, 'cloud-compass', 'unmanaged-remainder-total'],
        queryFn: () => apiClient.cloudResources.getCloudCompassUnmanagedRemainderTotal(organizationId),
        staleTime: STALE_TIME_MS
      }),
      // Only page 1 carries the total, so every later page is read against it.
      unmanagedRemainder: () =>
        infiniteQueryOptions({
          queryKey: [organizationId, 'cloud-compass', 'rows', { surface: 'unmanaged' }],
          queryFn: ({ pageParam }) =>
            apiClient.cloudResources.getCloudCompassUnmanagedRemainder(organizationId, pageParam),
          initialPageParam: 1,
          getNextPageParam: (lastPage, pages) =>
            sumBy(pages, ({ resources }) => resources.length) < (pages[0].total ?? 0) &&
            lastPage.resources.length === lastPage.pageSize &&
            lastPage.page < UNMANAGED_REMAINDER_MAX_PAGES
              ? lastPage.page + 1
              : undefined,
          ...ROW_LIST
        }),
      assetDetails: (path: AssetPath) => ({
        queryKey: [organizationId, 'cloud-compass', 'details', path],
        queryFn: () => apiClient.cloudResources.getCloudCompassAssetDetails(organizationId, path),
        ...DRAWER
      }),
      nativeRow: (path: AssetPath) => ({
        queryKey: [organizationId, 'cloud-compass', 'native-id', path],
        queryFn: () => apiClient.cloudResources.getCloudCompassNativeRow(organizationId, path),
        ...DRAWER
      }),
      related: (path: AssetPath) => ({
        queryKey: [organizationId, 'cloud-compass', 'related', path],
        queryFn: () => apiClient.cloudResources.getCloudCompassAssetRelated(organizationId, path),
        ...DRAWER
      }),
      // Re-checked on every dialog open, so a cached answer would defeat it.
      managedStatus: (resources: AssetPath[]) => ({
        queryKey: [organizationId, 'cloud-compass', 'managed-status', resources],
        queryFn: () => apiClient.cloudResources.checkCloudCompassManagedStatus(organizationId, resources),
        staleTime: 0,
        gcTime: 0
      }),
      assets: (query: CloudCompassAssetsQuery) => ({
        queryKey: [organizationId, 'cloud-compass', 'assets', query],
        queryFn: () => apiClient.cloudResources.getCloudCompassAssets(organizationId, query),
        ...ROW_LIST
      })
    }),
    [apiClient, organizationId]
  );
};

export const useFetchCloudCompassFilterValues = () => {
  const queryClient = useQueryClient();
  const { filterValues } = useCloudCompassQueryOptions();

  return useCallback(
    (dimension: string) => queryClient.fetchQuery(filterValues(dimension)),
    [queryClient, filterValues]
  );
};

export const useCloudCompassFilterValues = (dimensions: string[]) => {
  const { filterValues } = useCloudCompassQueryOptions();

  return useQueries({
    queries: dimensions.map(filterValues),
    combine: results =>
      Object.fromEntries(dimensions.map((dimension, index) => [dimension, results[index].data?.values ?? []]))
  });
};
